import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiStar } from "react-icons/fi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending Approval" },
  { key: "approved", label: "Approved" },
];

function initials(name = "") {
  return name
    .replace(/^(mr\.|mrs\.|ms\.)/i, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function TestimonialList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchItems = () => {
    setLoading(true);
    api
      .get("/testimonials/admin/all")
      .then((res) => setItems(res.data))
      .catch(() => toast.error("Failed to load testimonials"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete the review from "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/testimonials/admin/${id}`);
      toast.success("Testimonial deleted");
      setItems(items.filter((s) => s._id !== id));
    } catch (err) {
      toast.error("Failed to delete testimonial");
    }
  };

  const toggleApproved = async (item) => {
    try {
      const { data } = await api.put(`/testimonials/admin/${item._id}`, {
        approved: !item.approved,
      });
      setItems(items.map((s) => (s._id === item._id ? data : s)));
      toast.success(data.approved ? "Approved & published" : "Unpublished");
    } catch (err) {
      toast.error("Failed to update testimonial");
    }
  };

  const pendingCount = items.filter((i) => !i.approved).length;

  const filtered = items.filter((i) => {
    if (filter === "pending") return !i.approved;
    if (filter === "approved") return i.approved;
    return true;
  });

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl text-maroon-800">Testimonials & Reviews</h1>
          <p className="text-sm text-gray-500 mt-1">
            Approve devotee reviews submitted from the website and manage testimonials shown on the site.
          </p>
        </div>
        <Link
          to="/admin/testimonials/new"
          className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <FiPlus /> New Testimonial
        </Link>
      </div>

      {pendingCount > 0 && (
        <div className="mb-5 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
          <FiStar className="text-amber-500" />
          <span>
            <strong>{pendingCount}</strong> new review{pendingCount > 1 ? "s" : ""} awaiting approval.
          </span>
          <button
            onClick={() => setFilter("pending")}
            className="ml-auto font-semibold text-amber-900 hover:underline"
          >
            Review now
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5">
        {FILTERS.map((f) => {
          const count =
            f.key === "all"
              ? items.length
              : f.key === "pending"
              ? pendingCount
              : items.length - pendingCount;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                filter === f.key
                  ? "bg-maroon-800 text-gold-100 border-maroon-800"
                  : "bg-white text-gray-600 border-gold-200 hover:border-saffron-400"
              }`}
            >
              {f.label} ({count})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No testimonials here"
          subtitle="Approved reviews appear on the public Testimonials page and homepage."
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <div
              key={t._id}
              className={`bg-white rounded-2xl shadow-sm border p-5 flex flex-col ${
                t.approved ? "border-gray-100" : "border-amber-200 bg-amber-50/40"
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  {t.image ? (
                    <img
                      src={imgUrl(t.image)}
                      alt={t.name}
                      className="w-11 h-11 rounded-full object-cover border border-gold-200"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-maroon-gradient text-gold-200 flex items-center justify-center font-heading text-sm shadow-gold">
                      {initials(t.name)}
                    </div>
                  )}
                  <div>
                    <p className="font-heading text-maroon-800 text-sm leading-tight">{t.name}</p>
                    {t.location && <p className="text-xs text-saffron-600">{t.location}</p>}
                  </div>
                </div>
                {t.submittedByUser && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium uppercase tracking-wide">
                    User
                  </span>
                )}
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 text-gold-500 mb-2">
                {[...Array(5)].map((_, idx) => (
                  <FiStar
                    key={idx}
                    size={13}
                    fill={idx < t.rating ? "currentColor" : "none"}
                    className={idx < t.rating ? "" : "text-gold-200"}
                  />
                ))}
              </div>

              <p className="text-sm text-gray-600 italic line-clamp-4 mb-3 flex-1">“{t.text}”</p>

              {t.service && (
                <p className="text-[11px] text-gray-400 mb-3">
                  Service: <span className="text-gray-500">{t.service}</span>
                </p>
              )}

              <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-auto">
                <button
                  onClick={() => toggleApproved(t)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    t.approved
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                  }`}
                >
                  <FiCheck /> {t.approved ? "Approved" : "Approve"}
                </button>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/testimonials/edit/${t._id}`}
                    className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600"
                  >
                    <FiEdit2 />
                  </Link>
                  <button
                    onClick={() => handleDelete(t._id, t.name)}
                    className="p-2 rounded-lg hover:bg-gray-100 text-red-500"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
